import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Grid, IconButton, Stack, Typography, alpha } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const ACCENT = "#1565c0";

type FotosVisitaMultiUploaderProps = {
  files: File[];
  setFiles: (f: File[]) => void;
  max?: number;
  min?: number;
  label?: string;
  disabled?: boolean;
};

/**
 * Uploader multi-foto reusable (máx N, mín M): zona de arrastrar-y-soltar +
 * grilla de miniaturas con botón de quitar. Devuelve File[] al padre (el padre
 * sube a MinIO y persiste). Usado por todos los modales de fotos de visita
 * (ingreso / proceso / salida).
 */
export default function FotosVisitaMultiUploader({
  files,
  setFiles,
  max = 10,
  min = 2,
  label = "Fotos",
  disabled = false,
}: FotosVisitaMultiUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  // URLs de preview creadas de forma síncrona y revocadas al cambiar/desmontar
  // (evita fugas de memoria por createObjectURL en cada render).
  const urls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);

  const room = Math.max(0, max - files.length);
  const lleno = files.length >= max;
  const cumpleMin = files.length >= min;
  const faltan = Math.max(0, min - files.length);

  const addFiles = (picked: File[]) => {
    if (disabled) return;
    const imgs = picked.filter((f) => f.type.startsWith("image/"));
    if (!imgs.length) return;
    setFiles([...files, ...imgs.slice(0, room)]);
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []) as File[]);
    if (inputRef.current) inputRef.current.value = "";
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDrag(false);
    addFiles(Array.from(e.dataTransfer.files || []) as File[]);
  };
  const remove = (i: number) => setFiles(files.filter((_, idx) => idx !== i));

  return (
    <Box sx={{ width: "100%" }}>
      {/* Cabecera: label + contador con estado */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="body2" fontWeight={700}>
          {label}
        </Typography>
        <Chip
          size="small"
          icon={cumpleMin ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : undefined}
          color={cumpleMin ? "success" : "default"}
          variant={cumpleMin ? "filled" : "outlined"}
          label={`${files.length} / ${max}${!cumpleMin ? ` · mín ${min}` : ""}`}
          sx={{ fontWeight: 700 }}
        />
      </Stack>

      {/* Zona de carga (arrastrar-soltar o clic) */}
      {!lleno ? (
        <Box
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={`Agregar ${label}`}
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !disabled) {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          sx={{
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            border: "2px dashed",
            borderColor: drag ? ACCENT : "divider",
            bgcolor: drag ? alpha(ACCENT, 0.06) : "transparent",
            borderRadius: 2,
            px: 2,
            py: { xs: 2.5, sm: 3 },
            textAlign: "center",
            transition: "border-color .18s ease, background-color .18s ease",
            outline: "none",
            "&:hover": disabled
              ? {}
              : { borderColor: ACCENT, bgcolor: alpha(ACCENT, 0.04) },
            "&:focus-visible": {
              borderColor: ACCENT,
              boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.25)}`,
            },
          }}
        >
          <CloudUploadOutlinedIcon
            sx={{ fontSize: 40, color: drag ? ACCENT : "text.disabled" }}
          />
          <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
            Arrastra las fotos aquí o haz clic para seleccionar
          </Typography>
          <Typography variant="caption" color="text.secondary">
            JPG o PNG · puedes agregar {room} más (máx {max})
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "success.light",
            bgcolor: alpha("#2e7d32", 0.06),
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CheckCircleIcon sx={{ color: "success.main", fontSize: 20 }} />
          <Typography variant="body2" color="text.secondary">
            Alcanzaste el máximo de {max} fotos. Elimina alguna para reemplazarla.
          </Typography>
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={onPick}
      />

      {/* Grilla de miniaturas */}
      {files.length > 0 && (
        <Grid container spacing={1.25} sx={{ mt: 1.25 }}>
          {files.map((f, i) => (
            <Grid item xs={6} sm={4} md={3} key={`${f.name}-${i}`}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  aspectRatio: "4 / 3",
                  bgcolor: "action.hover",
                }}
              >
                <img
                  src={urls[i]}
                  alt={`${label} ${i + 1}`}
                  loading="lazy"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                <Chip
                  size="small"
                  label={i + 1}
                  sx={{
                    position: "absolute",
                    bottom: 6,
                    left: 6,
                    height: 20,
                    minWidth: 24,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: "#fff",
                  }}
                />
                <IconButton
                  size="small"
                  aria-label={`Eliminar ${label} ${i + 1}`}
                  onClick={() => remove(i)}
                  disabled={disabled}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    p: 0.5,
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    transition: "background-color .18s ease",
                    "&:hover": { bgcolor: "error.main" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Ayuda / error de mínimo */}
      {files.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Aún no has agregado fotos. Agrega mínimo {min}.
        </Typography>
      )}
      {files.length > 0 && faltan > 0 && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 1, display: "block", fontWeight: 600 }}
        >
          Falta{faltan === 1 ? "" : "n"} {faltan} foto{faltan === 1 ? "" : "s"} — mínimo {min}.
        </Typography>
      )}
    </Box>
  );
}
